import { Injectable } from '@angular/core';
import {
  PayrollCensusAttributeSnapshotCriteria,
  PayrollCensusAttributeCriterion,
  PayrollCensusCompleteness,
  PayrollCensusResponse,
  PayrollProviderCapabilities,
  PayrollProviderConnectionResponse,
  PayrollService,
  UKG_READY_PROVIDER_SLUG,
} from '../payroll/payroll.service';
import { UsersService } from '../users/users.service';

export type PayrollCensusInit = {
  company: any;
  providerConnection?: PayrollProviderConnectionResponse | null;
  criteriaSnapshot?: PayrollCensusAttributeSnapshotCriteria | null;
  teamId?: string;
  teamsMap?: Record<string, any>;
  selectedEmployeeIds?: any[];
  refreshUkgCensus?: boolean;
};

export type PayrollCensusSaveArgs = {
  brand: string;
  companyId: string;
  isEdit: boolean;
  teamId?: string;
  teamName: string;
  wellnessAmount: number;
  autoAddNewMembers: boolean;
  fieldsContain?: Record<string, any>;
  criteriaSnapshot?: PayrollCensusAttributeSnapshotCriteria | null;
};

type PayrollCensusLocalFilterField = {
  field: string;
  label: string;
  keys: string[];
};

const PAYCOR_PROVIDER_SLUG = 'paycor';
const COMPLETE_CENSUS: PayrollCensusCompleteness = {
  complete: true,
  employeeCollectionComplete: true,
  emailComplete: true,
  missingEmailCount: 0,
  warnings: [],
};
const PAYCOR_LOCAL_CENSUS_FILTER_FIELDS: PayrollCensusLocalFilterField[] = [
  {
    field: 'location',
    label: 'Location',
    keys: ['Location', 'location', 'WorkLocation', 'Work Location', 'workLocation', 'Default Location Full Path'],
  },
  {
    field: 'department',
    label: 'Department',
    keys: ['Department', 'department', 'Default Department Full Path', 'dept'],
  },
  {
    field: 'manager',
    label: 'Manager',
    keys: ['Manager', 'manager'],
  },
  {
    field: 'payGroup',
    label: 'Paygroup',
    keys: ['PayGroup', 'Pay Group', 'payGroup'],
  },
  {
    field: 'jobTitle',
    label: 'Job title',
    keys: ['JobTitle', 'Job Title', 'Payroll Job Title', 'jobTitle'],
  },
  {
    field: 'workerType',
    label: 'Worker Type',
    keys: ['WorkerType', 'Worker Type', 'workerType', 'Employee Type', 'employeeType', 'Employment Type', 'employmentType'],
  },
  {
    field: 'statusType',
    label: 'Status Type',
    keys: ['StatusType', 'Status Type', 'statusType', 'EmploymentType', 'Employment Type', 'employmentType'],
  },
  {
    field: 'flsa',
    label: 'FLSA',
    keys: ['FLSA', 'flsa', 'Flsa', 'FLSA Type', 'flsaType'],
  },
  {
    field: 'employmentStatus',
    label: 'Status',
    keys: [
      'EmployeeStatus',
      'Employee Status',
      'EmploymentStatus',
      'Employment Status',
      'SourceStatus',
      'Source Status',
      'employeeStatus',
      'employmentStatus',
      'sourceStatus',
      'status',
    ],
  },
];
const UKG_LOCAL_CENSUS_FILTER_FIELDS: PayrollCensusLocalFilterField[] = [
  {
    field: 'Default Location Full Path',
    label: 'Location',
    keys: ['Default Location Full Path', 'location'],
  },
  {
    field: 'Default Department Full Path',
    label: 'Department',
    keys: ['Default Department Full Path', 'department'],
  },
  {
    field: 'Default Division Full Path',
    label: 'Division',
    keys: ['Default Division Full Path', 'division'],
  },
  {
    field: 'Payroll Job Title',
    label: 'Payroll Job Title',
    keys: ['Payroll Job Title'],
  },
  {
    field: 'Pay Type',
    label: 'Pay Type',
    keys: ['Pay Type', 'payType'],
  },
  {
    field: 'Employee Type',
    label: 'Employee Type',
    keys: ['Employee Type', 'employeeType'],
  },
  {
    field: 'State',
    label: 'State',
    keys: ['State', 'state'],
  },
];

export class PayrollCensusSession {
  private static readonly FILTER_LOAD_DEBOUNCE_MS = 250;

  // Data / filters
  availableEmployees: any[] = [];
  completeness: PayrollCensusCompleteness = { ...COMPLETE_CENSUS };
  snapshotBuiltAt: string | null = null;
  availableFilters: any[] = [];
  originalAvailableFilters: Record<string, any> = {};
  originalFilterKeys: Record<string, string> = {};

  activeFilters: { key: string; value: string; label: string }[] = [];
  activeFiltersMap: Record<string, string[]> = {};
  private filterMatchedEmployeesCacheSource?: any[];
  private filterMatchedEmployeesCacheKey = '';
  private filterMatchedEmployeesCache: any[] = [];

  // Selection
  selected = new Set<string>();
  selectedEmails: Record<string, boolean> = {};

  // Context
  private company!: any;
  private providerConnection?: PayrollProviderConnectionResponse | null;
  private providerSlug = '';
  private teamsMap: Record<string, any> = {};
  private teamId?: string;
  private loadDataTimer?: ReturnType<typeof setTimeout>;
  private queuedLoadToken = 0;
  private loadWaiters = new Map<number, { resolve: () => void; reject: (error: any) => void }>();
  private flushQueue = Promise.resolve();
  private initRequestSerial = 0;
  private loadRequestSerial = 0;

  isLoadingInitialData = true;
  isLoadingEmployees = false;

  constructor(
    private payrollService: PayrollService,
    private usersService: UsersService,
  ) {}

  invalidate(): void {
    this.initRequestSerial++;
    this.loadRequestSerial++;
    if (this.loadDataTimer) {
      clearTimeout(this.loadDataTimer);
      this.loadDataTimer = undefined;
    }
    this.loadWaiters.forEach(waiter => waiter.resolve());
    this.loadWaiters.clear();
    this.queuedLoadToken = 0;
    this.flushQueue = Promise.resolve();

    this.availableEmployees = [];
    this.completeness = { ...COMPLETE_CENSUS };
    this.snapshotBuiltAt = null;
    this.availableFilters = [];
    this.originalAvailableFilters = {};
    this.originalFilterKeys = {};
    this.activeFilters = [];
    this.activeFiltersMap = {};
    this.invalidateFilterMatchedEmployeesCache();
    this.selected = new Set<string>();
    this.selectedEmails = {};
    this.isLoadingInitialData = true;
    this.isLoadingEmployees = false;

    this.company = undefined;
    this.providerConnection = null;
    this.providerSlug = '';
    this.teamId = undefined;
    this.teamsMap = {};
  }

  async init(args: PayrollCensusInit): Promise<void> {
    this.invalidate();
    const initRequestSerial = this.initRequestSerial;

    this.company = args.company;
    this.providerConnection = args.providerConnection;
    this.providerSlug = this.getProviderSlug(args.providerConnection);
    this.teamId = args.teamId;
    this.teamsMap = args.teamsMap || {};

    // Seed selected set from dialog data (same behavior as before)
    (args.selectedEmployeeIds || []).forEach((employee: any) => {
      const key = this.getEmployeeSelectionKey(employee);
      if (key) {
        this.selected.add(key);
        this.selectedEmails[key] = true;
      }
    });

    if (this.usesUkgLocalBrowsingFilters()) {
      await this.loadData({ refreshUkgCensus: !!args.refreshUkgCensus });
      if (initRequestSerial !== this.initRequestSerial) {
        return;
      }
      this.seedActiveFiltersFromSavedTeam(args.criteriaSnapshot);
    } else {
      await this.refreshFilters();
      if (initRequestSerial !== this.initRequestSerial) {
        return;
      }
      this.seedActiveFiltersFromSavedTeam(args.criteriaSnapshot);
      await this.loadData();
      if (initRequestSerial !== this.initRequestSerial) {
        return;
      }
      if (this.usesLocalCensusFilters()) {
        this.refreshLocalFilters();
      }
    }
  }

  async refreshUkgCensus(): Promise<void> {
    if (!this.usesUkgLocalBrowsingFilters()) {
      return;
    }

    await this.loadData({ refreshUkgCensus: true });
    this.retainAvailableSelections();
  }

  // ---------- Filters ----------
  private extractFilterKey(key: string): string {
    return key.replace('Default ', '').replace(' Full Path', '');
  }

  private normalizeFilterKey(key: string): string {
    return `${key || ''}`.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private getLocalFilterFieldByLabel(label: string): PayrollCensusLocalFilterField | undefined {
    const normalizedLabel = this.normalizeFilterKey(label);
    return PAYCOR_LOCAL_CENSUS_FILTER_FIELDS.find((field) => (
      this.normalizeFilterKey(field.label) === normalizedLabel
      || this.normalizeFilterKey(field.field) === normalizedLabel
    ));
  }

  private getLocalFilterFieldByCriteriaField(field: string): PayrollCensusLocalFilterField | undefined {
    const normalizedField = this.normalizeFilterKey(field);
    return PAYCOR_LOCAL_CENSUS_FILTER_FIELDS.find((candidate) => (
      this.normalizeFilterKey(candidate.field) === normalizedField
      || this.normalizeFilterKey(candidate.label) === normalizedField
    ));
  }

  private getEmployeeFilterValue(employee: any, field: PayrollCensusLocalFilterField): string {
    for (const key of field.keys) {
      const value = `${employee?.[key] ?? ''}`.trim();
      if (value) {
        return value;
      }
    }
    return '';
  }

  usesLocalCensusFilters(): boolean {
    return this.providerSlug === PAYCOR_PROVIDER_SLUG
      && this.supportsCapability('supportsInternalTeamCreationFromCensus')
      && !this.supportsCapability('supportsFilteredTeams');
  }

  private usesUkgLocalBrowsingFilters(): boolean {
    return this.providerSlug === UKG_READY_PROVIDER_SLUG
      && this.supportsCapability('supportsCensusFilters');
  }

  private usesLocalBrowsingFilters(): boolean {
    return this.usesLocalCensusFilters() || this.usesUkgLocalBrowsingFilters();
  }

  private getBrowsingFilterFieldByLabel(label: string): PayrollCensusLocalFilterField | undefined {
    const fields = this.usesUkgLocalBrowsingFilters()
      ? UKG_LOCAL_CENSUS_FILTER_FIELDS
      : PAYCOR_LOCAL_CENSUS_FILTER_FIELDS;
    const normalizedLabel = this.normalizeFilterKey(label);
    return fields.find((field) => (
      this.normalizeFilterKey(field.label) === normalizedLabel
      || this.normalizeFilterKey(field.field) === normalizedLabel
    ));
  }

  canUseFilterControls(): boolean {
    return this.supportsCapability('supportsCensusFilters') || this.usesLocalCensusFilters();
  }

  private getActiveFiltersForRequest(): Record<string, any> {
    const filtersForRequest: Record<string, any> = {};
    for (const [filterKey, filterValue] of Object.entries(this.activeFiltersMap)) {
      const requestFilterKey = this.originalFilterKeys[filterKey] || filterKey;
      filtersForRequest[requestFilterKey] = filterValue;
    }
    return filtersForRequest;
  }

  getFieldsContainForRequestSnapshot(): Record<string, any> {
    const filtersForRequest = this.getActiveFiltersForRequest();
    const snapshot: Record<string, any> = {};
    for (const [key, value] of Object.entries(filtersForRequest)) {
      snapshot[key] = Array.isArray(value) ? [...value] : value;
    }
    return snapshot;
  }

  getActiveCriteriaSnapshot(): PayrollCensusAttributeSnapshotCriteria | null {
    if (!this.usesLocalCensusFilters() || !this.hasActiveFiltersApplied()) {
      return null;
    }

    const criteria: PayrollCensusAttributeCriterion[] = [];
    for (const [filterLabel, rawValues] of Object.entries(this.activeFiltersMap || {})) {
      const field = this.getLocalFilterFieldByLabel(filterLabel);
      const values = (Array.isArray(rawValues) ? rawValues : [rawValues])
        .map((value) => `${value ?? ''}`.trim())
        .filter((value) => !!value);
      if (!field || !values.length) {
        continue;
      }

      criteria.push({
        field: field.field,
        label: field.label,
        operator: values.length === 1 ? 'equals' : 'in',
        value: values.length === 1 ? values[0] : values,
        displayValue: values.join(', '),
      });
    }

    if (!criteria.length) {
      return null;
    }

    return {
      providerSlug: PAYCOR_PROVIDER_SLUG,
      mode: 'snapshot',
      match: 'all',
      criteria,
      activeOnly: true,
      eligibleStatuses: ['Active'],
      missingEmailPolicy: 'skip',
    };
  }

  private async refreshFilters(): Promise<void> {
    if (!this.supportsCapability('supportsCensusFilters') || this.usesUkgLocalBrowsingFilters()) {
      this.originalAvailableFilters = {};
      this.originalFilterKeys = {};
      this.availableFilters = [];
      return;
    }

    const censusFieldOptions =
      await this.payrollService.getCensusFilterOptionsForPayrollProviderCompany(
        this.company?.id,
        this.providerSlug,
      );

    this.setAvailableFilterOptions(typeof censusFieldOptions?.plain === 'function'
      ? censusFieldOptions.plain()
      : (censusFieldOptions || {}));
  }

  private setAvailableFilterOptions(censusFieldOptions: Record<string, any>): void {
    this.originalAvailableFilters = censusFieldOptions || {};

    this.originalFilterKeys = {};
    for (const [key] of Object.entries(this.originalAvailableFilters)) {
      const newKey = this.extractFilterKey(key);
      this.originalFilterKeys[newKey] = key;
    }

    this.availableFilters = Object.entries(this.originalAvailableFilters)
      .filter(([_, value]) => {
        const valueAsArray = value as string[];
        return Array.isArray(valueAsArray) && valueAsArray.length > 0;
      })
      .map(([key, value]) => {
        const newKey = this.extractFilterKey(key);
        return [newKey, value] as [string, string[]];
      });
  }

  private refreshLocalFilters(): void {
    if (!this.usesLocalCensusFilters()) {
      return;
    }

    this.originalAvailableFilters = {};
    this.originalFilterKeys = {};
    this.availableFilters = PAYCOR_LOCAL_CENSUS_FILTER_FIELDS
      .map((field) => {
        const seen = new Set<string>();
        const values: string[] = [];
        for (const employee of this.availableEmployees || []) {
          const value = this.getEmployeeFilterValue(employee, field);
          if (!value) {
            continue;
          }
          const normalizedValue = value.toLowerCase();
          if (seen.has(normalizedValue)) {
            continue;
          }
          seen.add(normalizedValue);
          values.push(value);
        }

        values.sort((left, right) => left.localeCompare(right, undefined, {
          numeric: true,
          sensitivity: 'base',
        }));
        this.originalAvailableFilters[field.label] = values;
        this.originalFilterKeys[field.label] = field.field;
        return [field.label, values] as [string, string[]];
      })
      .filter((filter) => filter[1].length > 0);
  }

  private seedLocalFiltersFromCriteriaSnapshot(criteriaSnapshot: PayrollCensusAttributeSnapshotCriteria | null | undefined): boolean {
    const criteria = criteriaSnapshot?.criteria;
    if (this.usesLocalCensusFilters() && Array.isArray(criteria)) {
      criteria.forEach((criterion: PayrollCensusAttributeCriterion) => {
        const field = this.getLocalFilterFieldByCriteriaField(criterion?.field);
        const values = (Array.isArray(criterion?.value) ? criterion.value : [criterion?.value])
          .map((value) => `${value ?? ''}`.trim())
          .filter((value) => !!value);
        if (field && values.length) {
          const existingValues = this.activeFiltersMap[field.label] || [];
          this.addToActiveFilters(field.label, Array.from(new Set(existingValues.concat(values))));
        }
      });
      return this.hasActiveFiltersApplied();
    }
    return false;
  }

  private seedActiveFiltersFromSavedTeam(initialCriteriaSnapshot?: PayrollCensusAttributeSnapshotCriteria | null): void {
    if (this.seedLocalFiltersFromCriteriaSnapshot(initialCriteriaSnapshot)) {
      return;
    }

    if (!this.teamId) {
      return;
    }

    const teamConfig = this.teamsMap[this.teamId] || {};
    const criteriaSnapshot = teamConfig?.criteriaSnapshot;
    if (this.seedLocalFiltersFromCriteriaSnapshot(criteriaSnapshot)) {
      return;
    }

    const teamFilters =
      this.supportsCapability('supportsCensusFilters')
      && teamConfig?.originalFilters?.fieldsContain
        ? teamConfig.originalFilters.fieldsContain
        : {};

    for (const [key, value] of Object.entries(teamFilters) as [string, string[]][]) {
      const newKey = this.extractFilterKey(key);
      this.addToActiveFilters(newKey, value);
    }
  }

  private async loadData(options: { refreshUkgCensus?: boolean } = {}): Promise<void> {
    const requestSerial = ++this.loadRequestSerial;
    const hasProviderSideFilters = this.supportsCapability('supportsCensusFilters')
      && !this.usesLocalBrowsingFilters()
      && Object.keys(this.activeFiltersMap).length > 0;
    let nextAvailableEmployees: any[] = [];
    let nextCompleteness: PayrollCensusCompleteness = { ...COMPLETE_CENSUS };
    let nextUkgFilterOptions: Record<string, any> | null = null;
    let nextUkgSnapshotBuiltAt: string | null = null;
    this.isLoadingEmployees = true;

    try {
      if (hasProviderSideFilters) {
        const filtersForRequest = this.getActiveFiltersForRequest();
        nextAvailableEmployees = await this.payrollService.getFilteredCensusForPayrollProviderCompany(
          this.company?.id,
          { fieldsContain: filtersForRequest },
          this.providerSlug,
        );
      } else {
        const response = this.usesUkgLocalBrowsingFilters()
          ? await this.payrollService.getCensusForPayrollProviderCompany(
            this.company?.id,
            this.providerSlug,
            {
              includeFilterOptions: true,
              refresh: !!options.refreshUkgCensus,
            },
          )
          : await this.payrollService.getCensusForPayrollProviderCompany(
            this.company?.id,
            this.providerSlug,
          );
        const compatibleResponse = response as PayrollCensusResponse | any[];
        nextAvailableEmployees = Array.isArray(compatibleResponse)
          ? compatibleResponse
          : (Array.isArray(compatibleResponse?.employees) ? compatibleResponse.employees : []);
        if (!Array.isArray(compatibleResponse) && compatibleResponse?.completeness) {
          nextCompleteness = {
            ...COMPLETE_CENSUS,
            ...compatibleResponse.completeness,
            warnings: Array.isArray(compatibleResponse.completeness.warnings)
              ? compatibleResponse.completeness.warnings
              : [],
          };
        }
        if (!Array.isArray(compatibleResponse) && this.usesUkgLocalBrowsingFilters()) {
          nextUkgFilterOptions = compatibleResponse?.filterOptions || {};
          nextUkgSnapshotBuiltAt = typeof compatibleResponse?.snapshotBuiltAt === 'string'
            ? compatibleResponse.snapshotBuiltAt
            : null;
        }
      }

      if (requestSerial !== this.loadRequestSerial) {
        return;
      }

      if (nextUkgFilterOptions !== null) {
        this.setAvailableFilterOptions(nextUkgFilterOptions);
        this.snapshotBuiltAt = nextUkgSnapshotBuiltAt;
      }
      this.availableEmployees = nextAvailableEmployees;
      this.completeness = nextCompleteness;
      this.invalidateFilterMatchedEmployeesCache();
      this.isLoadingInitialData = false;
      this.refreshLocalFilters();
    } finally {
      if (requestSerial === this.loadRequestSerial) {
        this.isLoadingEmployees = false;
      }
    }
  }

  private queueLoadData(delayMs = PayrollCensusSession.FILTER_LOAD_DEBOUNCE_MS): Promise<void> {
    const token = ++this.queuedLoadToken;
    const queuedLoadPromise = new Promise<void>((resolve, reject) => {
      this.loadWaiters.set(token, { resolve, reject });
    });
    this.isLoadingEmployees = true;

    if (this.loadDataTimer) {
      clearTimeout(this.loadDataTimer);
    }

    this.loadDataTimer = setTimeout(() => {
      this.loadDataTimer = undefined;
      const flushToken = this.queuedLoadToken;
      this.flushQueue = this.flushQueue.then(() => this.flushQueuedLoad(flushToken));
    }, delayMs);

    return queuedLoadPromise;
  }

  private async flushQueuedLoad(flushToken: number): Promise<void> {
    try {
      await this.loadData();
      this.resolveLoadWaiters(flushToken);
    } catch (error: any) {
      this.rejectLoadWaiters(flushToken, error);
    }
  }

  private resolveLoadWaiters(upToToken: number): void {
    for (const [token, waiter] of Array.from(this.loadWaiters.entries())) {
      if (token > upToToken) {
        continue;
      }
      waiter.resolve();
      this.loadWaiters.delete(token);
    }
  }

  private rejectLoadWaiters(upToToken: number, error: any): void {
    for (const [token, waiter] of Array.from(this.loadWaiters.entries())) {
      if (token > upToToken) {
        continue;
      }
      waiter.reject(error);
      this.loadWaiters.delete(token);
    }
  }

  addToActiveFilters(filterKey: string, filterValue: string[]): void {
    if (Array.isArray(filterValue) && filterValue.length === 0) {
      delete this.activeFiltersMap[filterKey];
    } else {
      this.activeFiltersMap[filterKey] = filterValue;
    }

    this.activeFilters = Object.entries(this.activeFiltersMap).reduce(
      (acc: { key: string; value: string; label: string }[], [key, values]) => {
        const chips = (values || []).map((v) => ({ key, label: key, value: v }));
        return acc.concat(chips);
      },
      []
    );
    this.invalidateFilterMatchedEmployeesCache();
  }

  async handleFilterSelect(filterKey: string, filterValue: string | string[]): Promise<void> {
    if (!this.canUseFilterControls()) {
      return;
    }

    if (!Array.isArray(filterValue)) {
      this.addToActiveFilters(filterKey, [filterValue]);
    } else {
      this.addToActiveFilters(filterKey, filterValue);
    }
    if (this.supportsCapability('supportsCensusFilters') && !this.usesLocalBrowsingFilters()) {
      await this.queueLoadData();
    }
  }

  async clearFilter(key: string, value: string): Promise<void> {
    if (!this.canUseFilterControls()) {
      return;
    }

    const current = this.activeFiltersMap[key] || [];
    const idx = current.indexOf(value);
    if (idx > -1) {
      const next = current.slice(0, idx).concat(current.slice(idx + 1));
      this.addToActiveFilters(key, next);
      if (this.supportsCapability('supportsCensusFilters') && !this.usesLocalBrowsingFilters()) {
        await this.queueLoadData();
      }
    }
  }

  async clearAllFilters(): Promise<void> {
    if (!this.canUseFilterControls()) {
      return;
    }

    this.activeFilters = [];
    this.activeFiltersMap = {};
    this.invalidateFilterMatchedEmployeesCache();
    if (this.supportsCapability('supportsCensusFilters') && !this.usesLocalBrowsingFilters()) {
      await this.queueLoadData();
    }
  }

  hasActiveFiltersApplied(): boolean {
    return Object.keys(this.activeFiltersMap || {}).length > 0;
  }

  private employeeMatchesLocalFilters(employee: any): boolean {
    if (!this.usesLocalBrowsingFilters() || !this.hasActiveFiltersApplied()) {
      return true;
    }

    return Object.entries(this.activeFiltersMap || {}).every(([filterLabel, rawValues]) => {
      const field = this.getBrowsingFilterFieldByLabel(filterLabel);
      const selectedValues = (Array.isArray(rawValues) ? rawValues : [rawValues])
        .map((value) => `${value ?? ''}`.trim().toLowerCase())
        .filter((value) => !!value);
      if (!field || !selectedValues.length) {
        return true;
      }

      const employeeValue = this.getEmployeeFilterValue(employee, field).toLowerCase();
      return !!employeeValue && selectedValues.includes(employeeValue);
    });
  }

  private invalidateFilterMatchedEmployeesCache(): void {
    this.filterMatchedEmployeesCacheSource = undefined;
    this.filterMatchedEmployeesCacheKey = '';
    this.filterMatchedEmployeesCache = [];
  }

  getFilterMatchedEmployees(): any[] {
    const source = this.availableEmployees || [];
    const cacheKey = JSON.stringify({
      activeFilters: this.activeFiltersMap || {},
      localFilters: this.usesLocalBrowsingFilters(),
      providerSlug: this.providerSlug,
    });

    if (
      this.filterMatchedEmployeesCacheSource === source
      && this.filterMatchedEmployeesCacheKey === cacheKey
    ) {
      return this.filterMatchedEmployeesCache;
    }

    this.filterMatchedEmployeesCacheSource = source;
    this.filterMatchedEmployeesCacheKey = cacheKey;
    this.filterMatchedEmployeesCache = source.filter((employee) => this.employeeMatchesLocalFilters(employee));
    return this.filterMatchedEmployeesCache;
  }

  getFilteredEmployees(searchText = ''): any[] {
    const search = `${searchText || ''}`.trim().toLowerCase();
    const employees = this.getFilterMatchedEmployees();
    if (!search) {
      return employees;
    }

    return employees.filter((employee) => {
      const first = `${employee?.['First Name'] ?? employee?.FirstName ?? employee?.firstName ?? ''}`.toLowerCase();
      const last = `${employee?.['Last Name'] ?? employee?.LastName ?? employee?.lastName ?? ''}`.toLowerCase();
      const email = `${employee?.['Primary Email'] ?? employee?.PrimaryEmail ?? employee?.primaryEmail ?? employee?.email ?? employee?.userEmail ?? ''}`.toLowerCase();
      const full = `${first} ${last}`.trim();
      return full.includes(search) || email.includes(search);
    });
  }

  private normalizeEmail(email: string | undefined | null): string {
    return `${email || ''}`.trim().toLowerCase();
  }

  private getEmployeeSelectionKey(employee: any): string {
    return (
      employee?.['Primary Email'] ||
      employee?.PrimaryEmail ||
      employee?.primaryEmail ||
      employee?.email ||
      employee?.userEmail ||
      employee?.['Employee Id'] ||
      employee?.EmployeeId ||
      employee?.employeeId ||
      employee?.id ||
      ''
    );
  }

  private retainAvailableSelections(): void {
    const availableKeys = new Map<string, string>();
    for (const employee of this.availableEmployees || []) {
      const key = this.getEmployeeSelectionKey(employee);
      const normalizedKey = this.normalizeEmail(key);
      if (key && normalizedKey && !availableKeys.has(normalizedKey)) {
        availableKeys.set(normalizedKey, key);
      }
    }

    const retainedSelected = new Set<string>();
    const retainedSelectedEmails: Record<string, boolean> = {};
    for (const selectedKey of Array.from(this.selected || [])) {
      const currentKey = availableKeys.get(this.normalizeEmail(selectedKey));
      if (!currentKey) {
        continue;
      }
      retainedSelected.add(currentKey);
      retainedSelectedEmails[currentKey] = true;
    }

    this.selected = retainedSelected;
    this.selectedEmails = retainedSelectedEmails;
  }

  private isEmployeeActive(employee: any): boolean {
    const status = `${employee?.['Employee Status']
      ?? employee?.EmployeeStatus
      ?? employee?.['Employment Status']
      ?? employee?.EmploymentStatus
      ?? employee?.['Source Status']
      ?? employee?.SourceStatus
      ?? employee?.employeeStatus
      ?? employee?.employmentStatus
      ?? employee?.sourceStatus
      ?? employee?.status
      ?? ''}`.trim().toLowerCase();
    return status === '' || status === 'active';
  }

  private getEmployeeEmail(employee: any): string {
    return this.normalizeEmail(
      employee?.['Primary Email'] ||
      employee?.PrimaryEmail ||
      employee?.primaryEmail ||
      employee?.email ||
      employee?.userEmail
    );
  }

  private getAvailableEmployeeEmailSet(activeOnly = false): Set<string> {
    const employees = this.hasActiveFiltersApplied()
      ? this.getFilterMatchedEmployees()
      : (this.availableEmployees || []);
    const emails = employees
      .filter((employee) => !activeOnly || this.isEmployeeActive(employee))
      .map((employee) => this.getEmployeeEmail(employee))
      .filter((email) => !!email);
    return new Set(emails);
  }

  private getSelectedEmployeeEmails(): string[] {
    const availableEmployeeEmailByKey = new Map<string, string>();

    for (const employee of this.availableEmployees || []) {
      const selectionKey = this.getEmployeeSelectionKey(employee);
      const normalizedSelectionKey = this.normalizeEmail(selectionKey);
      const email = this.getEmployeeEmail(employee);

      if (!email) {
        continue;
      }

      // Selected rows may hold a provider employee ID, even when the row also has email.
      const employeeId = `${employee?.['Employee Id'] || employee?.EmployeeId || employee?.employeeId || employee?.id || ''}`;
      if (employeeId) {
        availableEmployeeEmailByKey.set(employeeId, email);
        availableEmployeeEmailByKey.set(this.normalizeEmail(employeeId), email);
      }
      if (selectionKey && !availableEmployeeEmailByKey.has(selectionKey)) {
        availableEmployeeEmailByKey.set(selectionKey, email);
      }

      if (normalizedSelectionKey && !availableEmployeeEmailByKey.has(normalizedSelectionKey)) {
        availableEmployeeEmailByKey.set(normalizedSelectionKey, email);
      }
    }

    const selectedEmails: string[] = [];
    const seen = new Set<string>();

    for (const selectedKey of Array.from(this.selected || [])) {
      const normalizedSelectedKey = this.normalizeEmail(selectedKey);
      const resolvedEmail = this.normalizeEmail(
        availableEmployeeEmailByKey.get(selectedKey)
          || availableEmployeeEmailByKey.get(normalizedSelectedKey)
          || selectedKey
      );

      if (!resolvedEmail || !resolvedEmail.includes('@') || seen.has(resolvedEmail)) {
        continue;
      }

      seen.add(resolvedEmail);
      selectedEmails.push(resolvedEmail);
    }

    return selectedEmails;
  }

  private getSelectedEmailSet(): Set<string> {
    return new Set(this.getSelectedEmployeeEmails());
  }

  areAllAvailableEmployeesSelected(): boolean {
    const availableEmailSet = this.getAvailableEmployeeEmailSet(true);
    if (!availableEmailSet.size) {
      return false;
    }
    const selectedEmailSet = this.getSelectedEmailSet();
    for (const email of availableEmailSet) {
      if (!selectedEmailSet.has(email)) {
        return false;
      }
    }
    return true;
  }

  isAutoAddEligible(): boolean {
    if (!this.hasActiveFiltersApplied()) {
      return false;
    }
    return this.areAllAvailableEmployeesSelected();
  }

  getAutoAddEligibilityMessage(): string {
    if (!this.hasActiveFiltersApplied()) {
      return 'Apply at least one filter to enable auto-add.';
    }
    if (!this.getAvailableEmployeeEmailSet(true).size) {
      return 'No active employees match the current filters.';
    }
    if (!this.areAllAvailableEmployeesSelected()) {
      return 'Select all active employees matching the current filters to enable auto-add.';
    }
    return '';
  }

  // ---------- Selection ----------
  toggleEmployee(email: string, checked: boolean): void {
    if (checked) {
      this.selectedEmails[email] = true;
      this.selected.add(email);
    } else {
      delete this.selectedEmails[email];
      this.selected.delete(email);
    }
  }

  selectAllVisible(visibleEmployees: any[]): void {
    visibleEmployees.forEach((e) => {
      const selectionKey = this.getEmployeeSelectionKey(e);
      if (selectionKey && this.getEmployeeEmail(e)) {
        this.selected.add(selectionKey);
        this.selectedEmails[selectionKey] = true;
      }
    });
  }

  deselectAllVisible(visibleEmployees: any[]): void {
    visibleEmployees.forEach((e) => {
      const selectionKey = this.getEmployeeSelectionKey(e);
      if (selectionKey) {
        this.selected.delete(selectionKey);
        delete this.selectedEmails[selectionKey];
      }
    });
  }

  // ---------- Saving ----------
  async saveTeam(args: PayrollCensusSaveArgs): Promise<any> {
    if (!args.teamName) {
      return;
    }

    const filtersForRequest = args.fieldsContain || this.getActiveFiltersForRequest();
    const criteriaSnapshot = args.criteriaSnapshot?.mode === 'snapshot'
      && Array.isArray(args.criteriaSnapshot.criteria)
      && args.criteriaSnapshot.criteria.length > 0
        ? args.criteriaSnapshot
        : null;

    const allSelected = this.areAllAvailableEmployeesSelected();
    const shouldUseExplicitEmployeeEmails =
      this.supportsCapability('supportsInternalTeamCreationFromCensus')
      && !this.supportsCapability('supportsFilteredTeams');
    const effectiveAutoAddNewMembers = !!args.autoAddNewMembers && this.isAutoAddEligible();

    if (criteriaSnapshot && !args.isEdit) {
      const body: Record<string, any> = {
        criteriaSnapshot,
        teamName: args.teamName,
        autoAddNewMembers: !!args.autoAddNewMembers,
      };

      if (args.wellnessAmount) {
        body.amount = args.wellnessAmount;
      }

      return this.payrollService.createTeamFromCensusAttributeSnapshotForCompany(
        args.brand,
        args.companyId,
        body,
      );
    }

    if (!this.selected.size) {
      return;
    }

    const selectedEmployeeEmails = this.getSelectedEmployeeEmails();
    if (!selectedEmployeeEmails.length) {
      return;
    }

    const body: Record<string, any> = {
      fieldsContain: filtersForRequest,
      teamName: args.teamName,
      autoAddNewMembers: effectiveAutoAddNewMembers,
    };

    if (args.wellnessAmount) {
      body.amount = args.wellnessAmount;
    }

    if (!allSelected || shouldUseExplicitEmployeeEmails) {
      body.employeeEmails = selectedEmployeeEmails;
    }

    if (args.isEdit) {
      return this.payrollService.updateTeamForCompany(
        args.brand,
        args.companyId,
        args.teamId || '',
        body
      );
    }

    // Create path differs depending on “all selected”
    if (allSelected && !shouldUseExplicitEmployeeEmails) {
      return this.payrollService.createTeamFromFilteredCensusForCompany(args.brand, args.companyId, body);
    }
    return this.payrollService.createTeamForCompanyFromEmployeeEmails(args.brand, args.companyId, body);
  }

  private getProviderSlug(connection: PayrollProviderConnectionResponse | null | undefined): string {
    return `${connection?.providerSlug || connection?.connection?.providerSlug || ''}`.trim();
  }

  private supportsCapability(capability: keyof PayrollProviderCapabilities): boolean {
    if (!this.providerConnection) {
      return true;
    }

    const capabilities = this.providerConnection.capabilities
      || this.providerConnection.provider?.capabilities
      || {};
    return capabilities[capability] === true;
  }
}

@Injectable({ providedIn: 'root' })
export class PayrollCensusService {
  constructor(
    private payrollService: PayrollService,
    private usersService: UsersService
  ) {}

  createSession(): PayrollCensusSession {
    return new PayrollCensusSession(this.payrollService, this.usersService);
  }
}
